// src/pages/AdminECCPage.jsx
// Admin Economic Control Center - Phase 3: Simulation & Analysis Only
//
// SYSTEM CONTRACT:
// - Admin-only access
// - Read-only model viewing + draft editing
// - Simulation is pure/offline (no writes)
// - No runtime impact, no reader-visible changes
//
// PRIME DIRECTIVE:
// - ECC is OBSERVATIONAL only
// - This UI is for ANALYSIS, not CONTROL

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Calculator, TrendingUp, TrendingDown, BarChart3, Settings, Eye, 
  DollarSign, Users, Clock, MessageSquare, AlertTriangle, Loader2,
  RefreshCw, Play, Edit3, Save, X, HelpCircle, ChevronDown, ChevronUp,
  Target, UserCheck, Zap, ArrowUpRight, ArrowDownRight, Minus,
  Activity, Database, FileText, CheckCircle, ShoppingBag, Lock, Sliders, Info
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { useNotification } from '@/contexts/NotificationContext';
import AdminNav from '@/components/AdminNav';
import AdminTopMenuBar from '@/components/AdminTopMenuBar';
import UniversalFooter from '@/components/UniversalFooter';
import PricingImpactProjection from '@/components/admin/ecc/PricingImpactProjection';
import { BACKEND_URL } from '@/config/api';

// BACKEND_URL imported from centralized config
const API = `${BACKEND_URL}/api`;

// ============================================================================
// REUSABLE COMPONENTS (Admin Design System)
// ============================================================================

const StatCard = ({ icon: Icon, label, value, color, subtext, trend }) => (
  <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
    <div className="flex items-center gap-3 mb-2">
      <Icon className={`w-5 h-5 ${color}`} />
      <span className="text-sm text-slate-400">{label}</span>
    </div>
    <div className="flex items-baseline gap-2">
      <div className="text-2xl font-bold text-white">{value}</div>
      {trend !== undefined && (
        <span className={`text-sm ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
          {trend >= 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    {subtext && <div className="text-xs text-slate-500 mt-1">{subtext}</div>}
  </div>
);

const Badge = ({ children, variant = 'default' }) => {
  const variants = {
    default: 'bg-slate-700/50 text-slate-300 border-slate-600/50',
    warning: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    success: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    info: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    danger: 'bg-red-500/20 text-red-400 border-red-500/30'
  };
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full border ${variants[variant]}`}>
      {children}
    </span>
  );
};

const SectionHeader = ({ icon: Icon, title, badge, color = 'text-purple-400', children }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-3">
      <Icon className={`w-6 h-6 ${color}`} />
      <h2 className="text-xl font-bold text-white">{title}</h2>
      {badge && <Badge variant="warning">{badge}</Badge>}
    </div>
    {children}
  </div>
);

const InsightBadge = ({ icon: Icon, label, variant = 'default' }) => {
  const variants = {
    default: 'bg-slate-700/50 text-slate-300 border-slate-600/50',
    listening: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    ai: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    cost_up: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    cost_down: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    payout_up: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    payout_down: 'bg-rose-500/20 text-rose-400 border-rose-500/30'
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full border ${variants[variant]}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

const RiskBadge = ({ risk }) => {
  const configs = {
    low: { color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', label: 'Low Risk' },
    medium: { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', label: 'Medium' },
    high: { color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'High Risk' }
  };
  const config = configs[risk] || configs.medium;
  return (
    <span className={`px-2 py-1 text-xs rounded-full border ${config.color}`}>
      {config.label}
    </span>
  );
};

// ============================================================================
// TRACK D: DRAFT MODEL EDITOR MODAL
// ============================================================================

const DraftModelEditor = ({ model, isOpen, onClose, onSave }) => {
  const { notifyError } = useNotification();
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Initialize form state from model
  const [formData, setFormData] = useState({
    peu_value_weight: model?.value_weights?.peu_value_weight || 1.0,
    aeu_value_weight: model?.value_weights?.aeu_value_weight || 1.0,
    rc_scalar: model?.rc_scalar || 1.0,
    platform_pct: (model?.revenue_split?.platform_pct || 0.4) * 100,
    publisher_pct: (model?.revenue_split?.publisher_pct || 0.4) * 100,
    author_pct: (model?.revenue_split?.author_pct || 0.2) * 100
  });
  
  // Reset form when model changes
  useEffect(() => {
    if (model) {
      setFormData({
        peu_value_weight: model.value_weights?.peu_value_weight || 1.0,
        aeu_value_weight: model.value_weights?.aeu_value_weight || 1.0,
        rc_scalar: model.rc_scalar || 1.0,
        platform_pct: (model.revenue_split?.platform_pct || 0.4) * 100,
        publisher_pct: (model.revenue_split?.publisher_pct || 0.4) * 100,
        author_pct: (model.revenue_split?.author_pct || 0.2) * 100
      });
      setErrors({});
    }
  }, [model]);
  
  // Validate revenue split sums to 100%
  const validateRevenueSplit = () => {
    const total = formData.platform_pct + formData.publisher_pct + formData.author_pct;
    if (Math.abs(total - 100) > 0.01) {
      return `Revenue split must total 100%. Current: ${total.toFixed(1)}%`;
    }
    return null;
  };
  
  const handleSave = async () => {
    // Validate
    const splitError = validateRevenueSplit();
    if (splitError) {
      setErrors({ split: splitError });
      notifyError(splitError);
      return;
    }
    
    setSaving(true);
    try {
      await onSave(model.id, {
        peu_value_weight: formData.peu_value_weight,
        aeu_value_weight: formData.aeu_value_weight,
        rc_scalar: formData.rc_scalar,
        platform_pct: formData.platform_pct / 100,
        publisher_pct: formData.publisher_pct / 100,
        author_pct: formData.author_pct / 100
      });
      console.log('[SUCCESS] ' + 'Draft model saved successfully');
      onClose();
    } catch (error) {
      console.error('Save failed:', error);
      // Handle Pydantic validation errors which may be arrays of objects
      const detail = error.response?.data?.detail;
      let errorMsg = 'Failed to save draft model';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail[0]?.msg || detail[0]?.message || 'Validation error';
      } else if (detail?.msg) {
        errorMsg = detail.msg;
      }
      notifyError(errorMsg);
    } finally {
      setSaving(false);
    }
  };
  
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors({});
  };
  
  const splitTotal = formData.platform_pct + formData.publisher_pct + formData.author_pct;
  const splitValid = Math.abs(splitTotal - 100) <= 0.01;
  
  if (!model) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            <Sliders className="w-6 h-6 text-purple-400" />
            Edit Draft Model: {model.name}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Adjust economic parameters for simulation. Changes affect simulations only.
          </DialogDescription>
        </DialogHeader>
        
        {/* Safety Banner */}
        <div className="flex items-center gap-3 px-4 py-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
          <div className="text-sm">
            <span className="font-semibold text-amber-400">Draft model</span>
            <span className="text-amber-300/80"> — changes affect simulations only. No runtime impact.</span>
          </div>
        </div>
        
        <div className="space-y-6 py-4">
          {/* Core Valuation Controls */}
          <div>
            <h3 className="text-sm font-semibold text-purple-400 mb-4 flex items-center gap-2">
              <Calculator className="w-4 h-4" />
              Core Valuation Controls
            </h3>
            
            <div className="space-y-5">
              {/* PEU Weight */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-300">PEU Weight</label>
                  <span className="text-sm font-bold text-white bg-slate-800 px-2 py-1 rounded">
                    {formData.peu_value_weight.toFixed(2)}
                  </span>
                </div>
                <Slider
                  value={[formData.peu_value_weight]}
                  onValueChange={([val]) => updateField('peu_value_weight', val)}
                  min={0.1}
                  max={2.0}
                  step={0.05}
                  className="w-full"
                />
                <p className="text-xs text-slate-500">
                  Controls how strongly listening time converts into credits. Higher = more credits per minute.
                </p>
              </div>
              
              {/* AEU Weight */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-300">AEU Weight</label>
                  <span className="text-sm font-bold text-white bg-slate-800 px-2 py-1 rounded">
                    {formData.aeu_value_weight.toFixed(2)}
                  </span>
                </div>
                <Slider
                  value={[formData.aeu_value_weight]}
                  onValueChange={([val]) => updateField('aeu_value_weight', val)}
                  min={0.1}
                  max={2.0}
                  step={0.05}
                  className="w-full"
                />
                <p className="text-xs text-slate-500">
                  Controls how strongly AI usage converts into credits. Higher = more credits per AI interaction.
                </p>
              </div>
              
              {/* RC Scalar */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-300">RC Scalar</label>
                  <span className="text-sm font-bold text-white bg-slate-800 px-2 py-1 rounded">
                    {formData.rc_scalar.toFixed(2)}
                  </span>
                </div>
                <Slider
                  value={[formData.rc_scalar]}
                  onValueChange={([val]) => updateField('rc_scalar', val)}
                  min={0.5}
                  max={1.5}
                  step={0.05}
                  className="w-full"
                />
                <p className="text-xs text-slate-500">
                  Global throttle applied after PEU/AEU scoring. Lower = more conservative credit burn.
                </p>
              </div>
            </div>
          </div>
          
          {/* Revenue Distribution */}
          <div>
            <h3 className="text-sm font-semibold text-emerald-400 mb-4 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Revenue Distribution (Simulation Only)
            </h3>
            
            <div className="space-y-4">
              {/* Split Total Indicator */}
              <div className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
                splitValid 
                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' 
                  : 'bg-red-500/10 border-red-500/30 text-red-400'
              }`}>
                <span className="text-sm font-medium">Revenue Split Total</span>
                <span className="text-sm font-bold">
                  {splitTotal.toFixed(1)}% {splitValid ? '✓' : '(must be 100%)'}
                </span>
              </div>
              
              {/* Platform % */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-300">Platform %</label>
                  <span className="text-sm font-bold text-white bg-slate-800 px-2 py-1 rounded">
                    {formData.platform_pct.toFixed(0)}%
                  </span>
                </div>
                <Slider
                  value={[formData.platform_pct]}
                  onValueChange={([val]) => updateField('platform_pct', val)}
                  min={0}
                  max={100}
                  step={1}
                  className="w-full"
                />
              </div>
              
              {/* Publisher % */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-300">Publisher %</label>
                  <span className="text-sm font-bold text-white bg-slate-800 px-2 py-1 rounded">
                    {formData.publisher_pct.toFixed(0)}%
                  </span>
                </div>
                <Slider
                  value={[formData.publisher_pct]}
                  onValueChange={([val]) => updateField('publisher_pct', val)}
                  min={0}
                  max={100}
                  step={1}
                  className="w-full"
                />
              </div>
              
              {/* Author % */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-300">Author %</label>
                  <span className="text-sm font-bold text-white bg-slate-800 px-2 py-1 rounded">
                    {formData.author_pct.toFixed(0)}%
                  </span>
                </div>
                <Slider
                  value={[formData.author_pct]}
                  onValueChange={([val]) => updateField('author_pct', val)}
                  min={0}
                  max={100}
                  step={1}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer with safety reminder */}
        <div className="flex items-center gap-2 px-3 py-2 bg-slate-800/50 rounded-lg text-xs text-slate-500 mb-4">
          <Info className="w-4 h-4 flex-shrink-0" />
          <span>No runtime impact. Pricing and user balances are unaffected by draft changes.</span>
        </div>
        
        <DialogFooter className="gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !splitValid}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Draft
              </>
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// MODEL CARD COMPONENT
// ============================================================================

const ModelCard = ({ model, isSelected, onSelect, onUpdateIntent, onEditDraft }) => {
  const [expanded, setExpanded] = useState(false);
  const [editingIntent, setEditingIntent] = useState(false);
  const [intent, setIntent] = useState(model?.intent || '');
  const [whoBenefits, setWhoBenefits] = useState(model?.intent_who_benefits || '');
  const [behavior, setBehavior] = useState(model?.intent_behavior || '');
  const [saving, setSaving] = useState(false);
  
  const isDraft = model.status === 'draft';
  const isLocked = model.status === 'active' || model.status === 'archived';
  
  const getStatusBadge = () => {
    const variants = {
      draft: { variant: 'info', label: 'Draft' },
      active: { variant: 'success', label: 'Active' },
      archived: { variant: 'default', label: 'Archived' }
    };
    const v = variants[model.status] || variants.draft;
    return <Badge variant={v.variant}>{v.label}</Badge>;
  };
  
  const getInsightBadges = () => {
    const badges = [];
    const peuWeight = model.value_weights?.peu_value_weight || 1.0;
    const aeuWeight = model.value_weights?.aeu_value_weight || 1.0;
    const rcScalar = model.rc_scalar || 1.0;
    
    if (peuWeight > aeuWeight) {
      badges.push({ icon: Clock, label: 'Listening Heavy', variant: 'listening' });
    }
    if (aeuWeight > peuWeight) {
      badges.push({ icon: MessageSquare, label: 'AI Query Heavy', variant: 'ai' });
    }
    if (aeuWeight > 1.0) {
      badges.push({ icon: TrendingUp, label: 'Higher AI Costs', variant: 'cost_up' });
    }
    if (aeuWeight < 1.0) {
      badges.push({ icon: TrendingDown, label: 'Lower AI Costs', variant: 'cost_down' });
    }
    if (rcScalar > 1.0) {
      badges.push({ icon: DollarSign, label: 'Higher Payouts', variant: 'payout_up' });
    }
    if (rcScalar < 1.0) {
      badges.push({ icon: DollarSign, label: 'Lower Payouts', variant: 'payout_down' });
    }
    return badges;
  };
  
  const handleSaveIntent = async () => {
    setSaving(true);
    try {
      await onUpdateIntent(model.id, { intent, intent_who_benefits: whoBenefits, intent_behavior: behavior });
      setEditingIntent(false);
      console.log('[SUCCESS] ' + 'Intent saved');
    } catch (e) {
      notifyError('Failed to save intent');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div 
      className={`bg-slate-800/50 rounded-xl border transition-all cursor-pointer ${
        isSelected ? 'border-purple-500/50 ring-1 ring-purple-500/30' : 'border-slate-700/50 hover:border-slate-600/50'
      }`}
      onClick={() => onSelect(model)}
    >
      {/* Header */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-white">{model.name}</h3>
            {getStatusBadge()}
            {isLocked && (
              <span className="flex items-center gap-1 text-xs text-slate-500">
                <Lock className="w-3 h-3" />
                Locked
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {isDraft && (
              <button
                onClick={(e) => { e.stopPropagation(); onEditDraft(model); }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white text-sm rounded-lg transition-colors"
              >
                <Sliders className="w-4 h-4" />
                Edit Draft
              </button>
            )}
            <span className="text-xs text-slate-500">v{model.version}</span>
          </div>
        </div>
        
        {model.notes && (
          <p className="text-sm text-slate-400 mb-3">{model.notes}</p>
        )}
        
        {/* Insight Badges */}
        <div className="flex flex-wrap gap-2 mb-3">
          {getInsightBadges().map((badge, i) => (
            <InsightBadge key={i} icon={badge.icon} label={badge.label} variant={badge.variant} />
          ))}
        </div>
        
        {/* Economic Parameters Grid */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-slate-900/50 rounded-lg p-2 text-center">
            <div className="text-xs text-slate-500">PEU Weight</div>
            <div className="text-sm font-semibold text-white">{model.value_weights?.peu_value_weight || 1.0}</div>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-2 text-center">
            <div className="text-xs text-slate-500">AEU Weight</div>
            <div className="text-sm font-semibold text-white">{model.value_weights?.aeu_value_weight || 1.0}</div>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-2 text-center">
            <div className="text-xs text-slate-500">RC Scalar</div>
            <div className="text-sm font-semibold text-white">{model.rc_scalar || 1.0}</div>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-2 text-center">
            <div className="text-xs text-slate-500">Platform %</div>
            <div className="text-sm font-semibold text-white">
              {((model.revenue_split?.platform_pct || 0) * 100).toFixed(0)}%
            </div>
          </div>
        </div>
        
        {/* Expand/Collapse */}
        <button 
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300 mt-3 transition-colors"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {expanded ? 'Hide Intent' : 'Show Intent'}
        </button>
      </div>
      
      {/* Expanded Intent Section */}
      {expanded && (
        <div className="border-t border-slate-700/50 p-4 bg-slate-900/30" onClick={e => e.stopPropagation()}>
          {!editingIntent ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-sm font-medium text-purple-400">
                  <Target className="w-4 h-4" />
                  Model Intent
                </div>
                {isDraft && (
                  <button 
                    onClick={() => setEditingIntent(true)}
                    className="text-xs text-slate-400 hover:text-white flex items-center gap-1 transition-colors"
                  >
                    <Edit3 className="w-3 h-3" /> Edit
                  </button>
                )}
              </div>
              
              {model.intent ? (
                <div className="space-y-2">
                  <p className="text-sm text-slate-300 italic">&ldquo;{model.intent}&rdquo;</p>
                  {model.intent_who_benefits && (
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <UserCheck className="w-3 h-3 text-emerald-400" />
                      <span>Benefits: {model.intent_who_benefits}</span>
                    </div>
                  )}
                  {model.intent_behavior && (
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <Zap className="w-3 h-3 text-amber-400" />
                      <span>Encourages: {model.intent_behavior}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic">No intent defined.</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm font-medium text-purple-400">
                  <Target className="w-4 h-4" />
                  Edit Intent
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => setEditingIntent(false)}
                    className="text-xs text-slate-400 hover:text-white p-1"
                    disabled={saving}
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={handleSaveIntent}
                    className="text-xs bg-purple-600 hover:bg-purple-500 text-white px-2 py-1 rounded flex items-center gap-1"
                    disabled={saving}
                  >
                    <Save className="w-3 h-3" />
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
              <textarea
                value={intent}
                onChange={e => setIntent(e.target.value)}
                placeholder="e.g., 'Optimized for deep listening sessions'"
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                rows={2}
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  value={whoBenefits}
                  onChange={e => setWhoBenefits(e.target.value)}
                  placeholder="Who benefits?"
                  className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                />
                <input
                  value={behavior}
                  onChange={e => setBehavior(e.target.value)}
                  placeholder="Encourages what behavior?"
                  className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SIMULATION PANEL
// ============================================================================

const SimulationPanel = ({ 
  models, 
  scenario, 
  setScenario, 
  onRunSimulation, 
  simulating,
  simulationResult,
  pricingProjection,
  loadingPricingProjection,
  showLegacyComparison,
  setShowLegacyComparison
}) => {
  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return '—';
    return Number(num).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };
  
  const formatCurrency = (num) => `$${formatNumber(num)}`;
  const formatPercent = (num) => `${formatNumber(num, 1)}%`;
  
  return (
    <div className="space-y-6">
      {/* Non-authoritative Banner */}
      <div className="flex items-center gap-3 px-4 py-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
        <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
        <div className="text-sm">
          <span className="font-semibold text-amber-400">Simulation Only</span>
          <span className="text-amber-300/80"> — These values are for analysis. No runtime impact on users or pricing.</span>
        </div>
      </div>
      
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Scenario Configuration */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="w-5 h-5 text-purple-400" />
            <h3 className="text-lg font-semibold text-white">Scenario Configuration</h3>
          </div>
          <p className="text-sm text-slate-400 mb-4">Define usage assumptions for the &ldquo;what-if&rdquo; simulation</p>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Model</label>
                <select 
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
                  value={scenario.model_id}
                  onChange={(e) => setScenario({...scenario, model_id: e.target.value})}
                >
                  <option value="">Select model...</option>
                  {models.map(m => (
                    <option key={m.id} value={m.id}>{m.name} ({m.status})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Period (Days)</label>
                <input 
                  type="number"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
                  value={scenario.period_days}
                  onChange={(e) => setScenario({...scenario, period_days: parseInt(e.target.value) || 30})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Total Users</label>
                <input 
                  type="number"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
                  value={scenario.total_users}
                  onChange={(e) => setScenario({...scenario, total_users: parseInt(e.target.value) || 1000})}
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Active User %</label>
                <input 
                  type="number"
                  step="0.1"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
                  value={scenario.active_user_pct}
                  onChange={(e) => setScenario({...scenario, active_user_pct: parseFloat(e.target.value) || 0.3})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Avg Listen Min/Day</label>
                <input 
                  type="number"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
                  value={scenario.avg_listen_minutes_per_day}
                  onChange={(e) => setScenario({...scenario, avg_listen_minutes_per_day: parseFloat(e.target.value) || 30})}
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Avg AI Questions/Day</label>
                <input 
                  type="number"
                  step="0.1"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
                  value={scenario.avg_questions_per_day}
                  onChange={(e) => setScenario({...scenario, avg_questions_per_day: parseFloat(e.target.value) || 2})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Avg Response Chars</label>
                <input 
                  type="number"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
                  value={scenario.avg_response_chars}
                  onChange={(e) => setScenario({...scenario, avg_response_chars: parseInt(e.target.value) || 500})}
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Credit Price (USD)</label>
                <input 
                  type="number"
                  step="0.01"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
                  value={scenario.credit_price_usd}
                  onChange={(e) => setScenario({...scenario, credit_price_usd: parseFloat(e.target.value) || 0.10})}
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Track E: Behavioral Configuration */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-emerald-400" />
            <h3 className="text-lg font-semibold text-white">Behavioral Model (Track E)</h3>
          </div>
          <p className="text-sm text-slate-400 mb-4">Tier-weighted consumption & bundle recovery</p>
          
          <div className="space-y-4">
            {/* Simulation Mode Toggle */}
            <div>
              <label className="text-xs text-slate-400 block mb-2">Simulation Mode</label>
              <div className="flex gap-2">
                <button
                  className={`flex-1 py-2 px-3 rounded-lg text-sm transition-colors ${
                    scenario.simulation_mode === 'distribution' 
                      ? 'bg-emerald-600 text-white' 
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                  onClick={() => setScenario({...scenario, simulation_mode: 'distribution'})}
                >
                  Cohort Distribution
                </button>
                <button
                  className={`flex-1 py-2 px-3 rounded-lg text-sm transition-colors ${
                    scenario.simulation_mode === 'single_tier' 
                      ? 'bg-emerald-600 text-white' 
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                  onClick={() => setScenario({...scenario, simulation_mode: 'single_tier'})}
                >
                  Single Tier
                </button>
              </div>
            </div>
            
            {scenario.simulation_mode === 'distribution' ? (
              /* Distribution Mode - Tier percentages */
              <div>
                <label className="text-xs text-slate-400 block mb-2">Tier Distribution (must = 100%)</label>
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <span className="text-xs text-slate-500">A La Carte</span>
                    <input 
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-sm text-white"
                      value={scenario.a_la_carte_pct}
                      onChange={(e) => setScenario({...scenario, a_la_carte_pct: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">Novice</span>
                    <input 
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-sm text-white"
                      value={scenario.novice_pct}
                      onChange={(e) => setScenario({...scenario, novice_pct: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">Pro Reader</span>
                    <input 
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-sm text-white"
                      value={scenario.pro_reader_pct}
                      onChange={(e) => setScenario({...scenario, pro_reader_pct: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">Book Worm</span>
                    <input 
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-sm text-white"
                      value={scenario.book_worm_pct}
                      onChange={(e) => setScenario({...scenario, book_worm_pct: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </div>
                <div className={`text-xs mt-1 ${
                  Math.abs((scenario.a_la_carte_pct + scenario.novice_pct + scenario.pro_reader_pct + scenario.book_worm_pct) - 1.0) <= 0.01
                    ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  Total: {((scenario.a_la_carte_pct + scenario.novice_pct + scenario.pro_reader_pct + scenario.book_worm_pct) * 100).toFixed(0)}%
                </div>
              </div>
            ) : (
              /* Single Tier Mode */
              <div>
                <label className="text-xs text-slate-400 block mb-2">Select Tier</label>
                <select
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white"
                  value={scenario.single_tier}
                  onChange={(e) => setScenario({...scenario, single_tier: e.target.value})}
                >
                  <option value="a_la_carte">A La Carte</option>
                  <option value="novice">Novice</option>
                  <option value="pro_reader">Pro Reader</option>
                  <option value="book_worm">Book Worm</option>
                </select>
              </div>
            )}
            
            {/* Tier Multipliers (collapsible) */}
            <details className="group">
              <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-300 flex items-center gap-1">
                <ChevronDown className="w-3 h-3 group-open:rotate-180 transition-transform" />
                Tier Multipliers
              </summary>
              <div className="grid grid-cols-4 gap-2 mt-2">
                <div>
                  <span className="text-xs text-slate-500">ALC</span>
                  <input 
                    type="number"
                    step="0.05"
                    className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                    value={scenario.multiplier_a_la_carte}
                    onChange={(e) => setScenario({...scenario, multiplier_a_la_carte: parseFloat(e.target.value) || 0.55})}
                  />
                </div>
                <div>
                  <span className="text-xs text-slate-500">Nov</span>
                  <input 
                    type="number"
                    step="0.05"
                    className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                    value={scenario.multiplier_novice}
                    onChange={(e) => setScenario({...scenario, multiplier_novice: parseFloat(e.target.value) || 0.75})}
                  />
                </div>
                <div>
                  <span className="text-xs text-slate-500">Pro</span>
                  <input 
                    type="number"
                    step="0.05"
                    className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                    value={scenario.multiplier_pro_reader}
                    onChange={(e) => setScenario({...scenario, multiplier_pro_reader: parseFloat(e.target.value) || 1.0})}
                  />
                </div>
                <div>
                  <span className="text-xs text-slate-500">BW</span>
                  <input 
                    type="number"
                    step="0.05"
                    className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                    value={scenario.multiplier_book_worm}
                    onChange={(e) => setScenario({...scenario, multiplier_book_worm: parseFloat(e.target.value) || 1.35})}
                  />
                </div>
              </div>
            </details>
            
            {/* Bundle Offset Config */}
            <div className="border-t border-slate-700/50 pt-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-slate-400">Bundle Offset Recovery</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={scenario.bundle_offset_enabled}
                    onChange={(e) => setScenario({...scenario, bundle_offset_enabled: e.target.checked})}
                  />
                  <div className="w-9 h-5 bg-slate-700 rounded-full peer peer-checked:bg-emerald-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                </label>
              </div>
              
              {scenario.bundle_offset_enabled && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500 block">Purchase Likelihood</label>
                    <input 
                      type="number"
                      step="0.05"
                      min="0"
                      max="1"
                      className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-sm text-white"
                      value={scenario.bundle_purchase_likelihood}
                      onChange={(e) => setScenario({...scenario, bundle_purchase_likelihood: parseFloat(e.target.value) || 0.60})}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 block">Coverage Ratio</label>
                    <input 
                      type="number"
                      step="0.05"
                      min="0"
                      max="1"
                      className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-sm text-white"
                      value={scenario.bundle_coverage_ratio}
                      onChange={(e) => setScenario({...scenario, bundle_coverage_ratio: parseFloat(e.target.value) || 0.75})}
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Track E4: Cost Inputs */}
            <div className="border-t border-slate-700/50 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Calculator className="w-3 h-3 text-amber-400" />
                <span className="text-xs text-slate-400">Cost Assumptions (Track E4)</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 block">TTS $/1k chars</label>
                  <input 
                    type="number"
                    step="0.001"
                    min="0"
                    className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-sm text-white"
                    value={scenario.tts_cost_per_1k_chars}
                    onChange={(e) => setScenario({...scenario, tts_cost_per_1k_chars: parseFloat(e.target.value) || 0.015})}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block">LLM $/1k chars</label>
                  <input 
                    type="number"
                    step="0.001"
                    min="0"
                    className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-sm text-white"
                    value={scenario.llm_cost_per_1k_chars}
                    onChange={(e) => setScenario({...scenario, llm_cost_per_1k_chars: parseFloat(e.target.value) || 0.005})}
                  />
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-1">Estimates for Cost vs Value analysis</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Run Simulation Button - Full Width */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5">
        <button 
          className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-500 text-white py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={onRunSimulation}
          disabled={simulating || !scenario.model_id}
        >
          {simulating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Running Simulation...
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              Run Simulation
            </>
          )}
        </button>
      </div>
        
        {/* Simulation Results */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              <h3 className="text-lg font-semibold text-white">Simulation Outputs</h3>
            </div>
            {simulationResult && (
              <button
                onClick={() => setShowLegacyComparison(!showLegacyComparison)}
                className="text-xs text-slate-400 hover:text-white flex items-center gap-1 transition-colors"
              >
                <Eye className="w-3 h-3" />
                {showLegacyComparison ? 'Hide' : 'Show'} Legacy Comparison
              </button>
            )}
          </div>
          
          {simulationResult ? (
            <div className="space-y-4">
              {/* Consumption */}
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-blue-400" />
                  <span className="text-sm font-medium text-blue-400">Consumption</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-400">PEUs:</span> <span className="text-white font-medium">{formatNumber(simulationResult.consumption?.total_peus)}</span></div>
                  <div><span className="text-slate-400">AEUs:</span> <span className="text-white font-medium">{formatNumber(simulationResult.consumption?.total_aeus)}</span></div>
                  <div><span className="text-slate-400">Score:</span> <span className="text-white font-medium">{formatNumber(simulationResult.consumption?.total_consumption_score)}</span></div>
                  <div><span className="text-slate-400">ECC RCs:</span> <span className="text-white font-medium">{formatNumber(simulationResult.consumption?.total_ecc_reader_credits)}</span></div>
                </div>
              </div>
              
              {/* Legacy Comparison */}
              {showLegacyComparison && simulationResult.legacy_comparison && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-amber-400" />
                      <span className="text-sm font-medium text-amber-400">vs Legacy System</span>
                    </div>
                    <span className={`text-sm font-semibold ${simulationResult.legacy_comparison.delta_ecc_minus_legacy >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {simulationResult.legacy_comparison.delta_ecc_minus_legacy >= 0 ? '+' : ''}
                      {formatPercent(simulationResult.legacy_comparison.delta_pct)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-slate-400">Legacy RCs:</span> <span className="text-white font-medium">{formatNumber(simulationResult.legacy_comparison.total_legacy_reader_credits)}</span></div>
                    <div>
                      <span className="text-slate-400">Delta:</span> 
                      <span className={`font-medium ml-1 ${simulationResult.legacy_comparison.delta_ecc_minus_legacy >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {simulationResult.legacy_comparison.delta_ecc_minus_legacy >= 0 ? '+' : ''}{formatNumber(simulationResult.legacy_comparison.delta_ecc_minus_legacy)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Financials */}
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm font-medium text-emerald-400">Financials</span>
                  </div>
                  <span className="text-sm font-semibold text-emerald-400">
                    {formatPercent(simulationResult.financials?.gross_margin_pct)} Margin
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-400">Revenue:</span> <span className="text-white font-medium">{formatCurrency(simulationResult.financials?.gross_revenue_usd)}</span></div>
                  <div><span className="text-slate-400">Cost:</span> <span className="text-white font-medium">{formatCurrency(simulationResult.financials?.total_cost_usd)}</span></div>
                  <div><span className="text-slate-400">Margin:</span> <span className="text-white font-medium">{formatCurrency(simulationResult.financials?.gross_margin_usd)}</span></div>
                </div>
              </div>
              
              {/* Track E3: Value Pools */}
              {simulationResult.value_pools && (
                <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-cyan-400" />
                      <span className="text-sm font-medium text-cyan-400">Value Attribution (Track E3)</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-800/50 rounded-lg p-3">
                      <div className="text-xs text-slate-500 mb-1">PEU Revenue (Listening)</div>
                      <div className="text-lg font-semibold text-white">{formatCurrency(simulationResult.value_pools.peu_revenue_usd)}</div>
                      <div className="text-xs text-cyan-400">{simulationResult.value_pools.peu_pct_of_total}% of total</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3">
                      <div className="text-xs text-slate-500 mb-1">AEU Revenue (AI)</div>
                      <div className="text-lg font-semibold text-white">{formatCurrency(simulationResult.value_pools.aeu_revenue_usd)}</div>
                      <div className="text-xs text-cyan-400">{simulationResult.value_pools.aeu_pct_of_total}% of total</div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Track E3: Rights Holder Split */}
              <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-purple-400" />
                  <span className="text-sm font-medium text-purple-400">Revenue Split (Track E3)</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-slate-400 mb-2">Platform</div>
                    <div className="text-lg font-semibold text-white mb-1">{formatCurrency(simulationResult.revenue_split?.platform_revenue_usd)}</div>
                    <div className="text-xs text-slate-500 space-y-1">
                      <div>From PEU (40%): {formatCurrency(simulationResult.revenue_split?.platform_from_peu_usd)}</div>
                      <div>From AEU (60%): {formatCurrency(simulationResult.revenue_split?.platform_from_aeu_usd)}</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 mb-2">Rights Holder</div>
                    <div className="text-lg font-semibold text-white mb-1">{formatCurrency(simulationResult.revenue_split?.rights_holder_revenue_usd)}</div>
                    <div className="text-xs text-slate-500 space-y-1">
                      <div>From PEU (60%): {formatCurrency(simulationResult.revenue_split?.rights_from_peu_usd)}</div>
                      <div>From AEU (40%): {formatCurrency(simulationResult.revenue_split?.rights_from_aeu_usd)}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Track E4: Effective Pricing (Renamed) */}
              {simulationResult.effective_pricing && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <DollarSign className="w-4 h-4 text-amber-400" />
                    <span className="text-sm font-medium text-amber-400">Effective Value Attribution (Track E4)</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                      <div className="text-xs text-slate-500 mb-1">Per Listen Hour</div>
                      <div className="text-white font-semibold">{formatCurrency(simulationResult.effective_pricing.effective_revenue_per_listen_hour, 4)}</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                      <div className="text-xs text-slate-500 mb-1">Per AI Question</div>
                      <div className="text-white font-semibold">{formatCurrency(simulationResult.effective_pricing.effective_revenue_per_ai_question, 4)}</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                      <div className="text-xs text-slate-500 mb-1">Per 1k AI Chars</div>
                      <div className="text-white font-semibold">{formatCurrency(simulationResult.effective_pricing.implied_value_per_1k_ai_chars, 4)}</div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2 italic">* These are effective revenue per unit, not delivery costs</p>
                </div>
              )}
              
              {/* Track E4: Cost vs Value Panel */}
              {simulationResult.cost_vs_value && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm font-medium text-emerald-400">Cost vs Value per AI Question</span>
                    </div>
                    <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-full">
                      {simulationResult.cost_vs_value.margin_pct_per_ai_question}% Margin
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Delivery Cost</div>
                      <div className="text-red-400 font-semibold">{formatCurrency(simulationResult.cost_vs_value.delivery_cost_per_ai_question, 4)}</div>
                      <div className="text-xs text-slate-600 mt-1">
                        TTS: ${simulationResult.cost_vs_value.tts_cost_per_1k_chars}/1k<br/>
                        LLM: ${simulationResult.cost_vs_value.llm_cost_per_1k_chars}/1k
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Revenue Value</div>
                      <div className="text-emerald-400 font-semibold">{formatCurrency(simulationResult.cost_vs_value.value_per_ai_question, 4)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Margin</div>
                      <div className="text-white font-semibold">{formatCurrency(simulationResult.cost_vs_value.margin_per_ai_question, 4)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Margin %</div>
                      <div className="text-emerald-400 font-bold text-lg">{simulationResult.cost_vs_value.margin_pct_per_ai_question}%</div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2 italic">* Costs are estimates for analysis only. Adjust in scenario config.</p>
                </div>
              )}
              
              {/* Track E: Tier Projections Table */}
              {simulationResult.tier_projections && simulationResult.tier_projections.length > 0 && (
                <div className="bg-slate-700/30 border border-slate-600/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4 text-cyan-400" />
                      <span className="text-sm font-medium text-cyan-400">Tier Behavior Projection</span>
                    </div>
                    <span className="text-xs text-slate-500">Mode: {simulationResult.simulation_mode}</span>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-slate-400 border-b border-slate-600/50">
                          <th className="text-left py-2 px-2">Tier</th>
                          <th className="text-right py-2 px-2">Users</th>
                          <th className="text-right py-2 px-2">RC/Mo</th>
                          <th className="text-right py-2 px-2">Allowance</th>
                          <th className="text-right py-2 px-2">Excess</th>
                          <th className="text-right py-2 px-2">Recovered</th>
                          <th className="text-right py-2 px-2">Net Excess</th>
                          <th className="text-center py-2 px-2">Risk</th>
                        </tr>
                      </thead>
                      <tbody>
                        {simulationResult.tier_projections.map((tp, idx) => (
                          <tr key={idx} className="border-b border-slate-700/30 hover:bg-slate-700/20">
                            <td className="py-2 px-2 text-white font-medium">{tp.tier_name}</td>
                            <td className="py-2 px-2 text-right text-slate-300">{tp.user_count}</td>
                            <td className="py-2 px-2 text-right text-slate-300">{formatNumber(tp.rc_per_user_month, 1)}</td>
                            <td className="py-2 px-2 text-right text-slate-300">{tp.tier_allowance}</td>
                            <td className="py-2 px-2 text-right text-amber-400">{formatNumber(tp.excess_rc, 0)}</td>
                            <td className="py-2 px-2 text-right text-emerald-400">{formatNumber(tp.recovered_rc, 0)}</td>
                            <td className="py-2 px-2 text-right text-red-400">{formatNumber(tp.net_excess_rc, 0)}</td>
                            <td className="py-2 px-2 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                tp.risk === 'low' ? 'bg-emerald-500/20 text-emerald-400' :
                                tp.risk === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                                'bg-red-500/20 text-red-400'
                              }`}>
                                {tp.risk.toUpperCase()}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Bundle Offset Summary */}
                  {simulationResult.bundle_offset?.enabled && (
                    <div className="mt-3 pt-3 border-t border-slate-600/30">
                      <div className="flex items-center gap-2 mb-2">
                        <ShoppingBag className="w-3 h-3 text-emerald-400" />
                        <span className="text-xs text-slate-400">Bundle Recovery Summary</span>
                      </div>
                      <div className="grid grid-cols-4 gap-3 text-xs">
                        <div>
                          <span className="text-slate-500 block">Total Excess</span>
                          <span className="text-amber-400 font-medium">{formatNumber(simulationResult.bundle_offset.total_excess_rc, 0)} RC</span>
                        </div>
                        <div>
                          <span className="text-slate-500 block">Recovered</span>
                          <span className="text-emerald-400 font-medium">{formatNumber(simulationResult.bundle_offset.total_recovered_rc, 0)} RC</span>
                        </div>
                        <div>
                          <span className="text-slate-500 block">Net Excess</span>
                          <span className="text-red-400 font-medium">{formatNumber(simulationResult.bundle_offset.total_net_excess_rc, 0)} RC</span>
                        </div>
                        <div>
                          <span className="text-slate-500 block">Bundle Revenue</span>
                          <span className="text-cyan-400 font-medium">{formatCurrency(simulationResult.bundle_offset.total_bundle_revenue)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <Play className="w-10 h-10 mb-3 opacity-50" />
              <p>Configure scenario and run simulation to see results</p>
            </div>
          )}
        </div>
      
      {/* Pricing Impact Projection */}
      {(simulationResult || loadingPricingProjection) && (
        <PricingProjectionPanel 
          projection={pricingProjection} 
          loading={loadingPricingProjection} 
        />
      )}
    </div>
  );
};

// ============================================================================
// PRICING PROJECTION PANEL (Redesigned)
// ============================================================================

const PricingProjectionPanel = ({ projection, loading }) => {
  if (loading) {
    return (
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6 text-center">
        <Loader2 className="w-6 h-6 animate-spin text-purple-400 mx-auto mb-2" />
        <p className="text-slate-400 text-sm">Computing pricing projection...</p>
      </div>
    );
  }
  
  if (!projection) return null;
  
  const { meta, derived, subscription_tiers, credit_bundles } = projection;
  
  const formatCurrency = (val, decimals = 2) => {
    if (val === null || val === undefined) return '—';
    return `$${Number(val).toFixed(decimals)}`;
  };
  
  return (
    <div className="bg-gradient-to-br from-purple-900/20 to-slate-900/40 rounded-xl border border-purple-500/20 p-6">
      <SectionHeader icon={ShoppingBag} title="Pricing Impact Projection" badge="Projection Only" color="text-purple-400" />
      
      <div className="flex items-center gap-3 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-lg mb-6 text-sm">
        <HelpCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
        <span className="text-purple-300">
          Derived from simulation. Uses current tiers/bundles as defined in Pricing admin. No runtime impact.
        </span>
      </div>
      
      {/* Effective Unit Pricing */}
      {derived?.effective && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-slate-400 mb-3">Effective Unit Pricing</h4>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-slate-800/50 rounded-lg p-4 text-center border border-slate-700/50">
              <Clock className="w-6 h-6 mx-auto mb-2 text-purple-400" />
              <div className="text-xl font-bold text-white">{formatCurrency(derived.effective.usd_per_listen_hour, 4)}</div>
              <div className="text-xs text-slate-500">per listening hour</div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4 text-center border border-slate-700/50">
              <MessageSquare className="w-6 h-6 mx-auto mb-2 text-purple-400" />
              <div className="text-xl font-bold text-white">{formatCurrency(derived.effective.usd_per_ai_question, 4)}</div>
              <div className="text-xs text-slate-500">per AI question</div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4 text-center border border-slate-700/50">
              <FileText className="w-6 h-6 mx-auto mb-2 text-purple-400" />
              <div className="text-xl font-bold text-white">{formatCurrency(derived.effective.usd_per_1000_chars, 4)}</div>
              <div className="text-xs text-slate-500">per 1,000 AI chars</div>
            </div>
          </div>
        </div>
      )}
      
      {/* Subscription Tiers */}
      {subscription_tiers && subscription_tiers.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4" /> Subscription Tier Performance
          </h4>
          <div className="bg-slate-800/30 rounded-lg overflow-hidden border border-slate-700/50">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700/50 text-slate-400">
                  <th className="text-left py-3 px-4 font-medium">Tier</th>
                  <th className="text-right py-3 px-4 font-medium">Price</th>
                  <th className="text-right py-3 px-4 font-medium">Credits</th>
                  <th className="text-right py-3 px-4 font-medium">Proj. Use</th>
                  <th className="text-right py-3 px-4 font-medium">Headroom</th>
                  <th className="text-center py-3 px-4 font-medium">Risk</th>
                </tr>
              </thead>
              <tbody>
                {subscription_tiers.map((tier, i) => (
                  <tr key={tier.tier_id || i} className="border-b border-slate-700/30">
                    <td className="py-3 px-4 text-white font-medium">{tier.name}</td>
                    <td className="text-right py-3 px-4 text-slate-300">{formatCurrency(tier.monthly_price_usd)}/mo</td>
                    <td className="text-right py-3 px-4 text-slate-300">{tier.monthly_credits}</td>
                    <td className="text-right py-3 px-4 text-slate-300">{tier.projected_monthly_consumption_credits?.toFixed(1)}</td>
                    <td className="text-right py-3 px-4">
                      <span className={tier.headroom_credits < 0 ? 'text-red-400' : 'text-emerald-400'}>
                        {tier.headroom_credits >= 0 ? '+' : ''}{tier.headroom_credits?.toFixed(1)}
                      </span>
                    </td>
                    <td className="text-center py-3 px-4">
                      <RiskBadge risk={tier.risk} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Credit Bundles */}
      {credit_bundles && credit_bundles.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
            <ShoppingBag className="w-4 h-4" /> Credit Bundle Margins
          </h4>
          <div className="bg-slate-800/30 rounded-lg overflow-hidden border border-slate-700/50">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700/50 text-slate-400">
                  <th className="text-left py-3 px-4 font-medium">Bundle</th>
                  <th className="text-right py-3 px-4 font-medium">Credits</th>
                  <th className="text-right py-3 px-4 font-medium">Price</th>
                  <th className="text-right py-3 px-4 font-medium">Proj. Cost</th>
                  <th className="text-right py-3 px-4 font-medium">Margin</th>
                </tr>
              </thead>
              <tbody>
                {credit_bundles.map((bundle, i) => (
                  <tr key={bundle.bundle_id || i} className="border-b border-slate-700/30">
                    <td className="py-3 px-4">
                      <span className="text-white font-medium">{bundle.name}</span>
                      {bundle.badge && <Badge variant="warning" className="ml-2">{bundle.badge}</Badge>}
                    </td>
                    <td className="text-right py-3 px-4 text-slate-300">{bundle.credits}</td>
                    <td className="text-right py-3 px-4 text-slate-300">{formatCurrency(bundle.price_usd)}</td>
                    <td className="text-right py-3 px-4 text-slate-500">{formatCurrency(bundle.projected_cost_usd, 4)}</td>
                    <td className="text-right py-3 px-4">
                      <span className="text-emerald-400 font-medium">{bundle.margin_pct?.toFixed(1)}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Cost per credit: {formatCurrency(derived?.cost_per_credit || 0, 6)} (from simulation)
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// ANALYTICS SECTION
// ============================================================================

const AnalyticsSection = ({ summaryStats, dailyBreakdown, onRefresh }) => {
  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return '—';
    return Number(num).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };
  
  return (
    <div className="space-y-6">
      {/* Non-authoritative Banner */}
      <div className="flex items-center gap-3 px-4 py-3 bg-blue-500/10 border border-blue-500/20 rounded-xl">
        <HelpCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
        <div className="text-sm">
          <span className="font-semibold text-blue-400">Shadow Analytics</span>
          <span className="text-blue-300/80"> — Real engagement data processed through ECC shadow calculations.</span>
        </div>
      </div>
      
      {/* Summary Stats */}
      {summaryStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Activity} label="Total Events" value={formatNumber(summaryStats.total_events, 0)} color="text-purple-400" />
          <StatCard icon={Calculator} label="ECC Credits" value={formatNumber(summaryStats.total_ecc_credits)} color="text-emerald-400" />
          <StatCard icon={Database} label="Legacy Credits" value={formatNumber(summaryStats.total_legacy_credits)} color="text-blue-400" />
          <StatCard 
            icon={TrendingUp} 
            label="ECC vs Legacy Delta" 
            value={`${summaryStats.delta_pct >= 0 ? '+' : ''}${formatNumber(summaryStats.delta_pct, 1)}%`}
            color={summaryStats.delta_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}
          />
        </div>
      )}
      
      {/* Daily Breakdown */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-purple-400" />
            Daily Shadow Events (Last 7 Days)
          </h3>
          <button onClick={onRefresh} className="text-xs text-slate-400 hover:text-white flex items-center gap-1 transition-colors">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
        
        {dailyBreakdown && dailyBreakdown.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700/50 text-slate-400">
                  <th className="text-left py-3 font-medium">Date</th>
                  <th className="text-right py-3 font-medium">Events</th>
                  <th className="text-right py-3 font-medium">PEUs</th>
                  <th className="text-right py-3 font-medium">AEUs</th>
                  <th className="text-right py-3 font-medium">ECC RCs</th>
                  <th className="text-right py-3 font-medium">Legacy RCs</th>
                  <th className="text-right py-3 font-medium">Delta</th>
                </tr>
              </thead>
              <tbody>
                {dailyBreakdown.map((day) => {
                  const delta = (day.ecc_credits || 0) - (day.legacy_credits || 0);
                  return (
                    <tr key={day.date} className="border-b border-slate-700/30">
                      <td className="py-3 text-white">{day.date}</td>
                      <td className="text-right py-3 text-slate-300">{day.events}</td>
                      <td className="text-right py-3 text-slate-300">{formatNumber(day.peus)}</td>
                      <td className="text-right py-3 text-slate-300">{formatNumber(day.aeus)}</td>
                      <td className="text-right py-3 text-slate-300">{formatNumber(day.ecc_credits)}</td>
                      <td className="text-right py-3 text-slate-300">{formatNumber(day.legacy_credits)}</td>
                      <td className={`text-right py-3 font-medium ${delta >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {delta >= 0 ? '+' : ''}{formatNumber(delta)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-slate-500">
            <BarChart3 className="w-10 h-10 mb-3 opacity-50" />
            <p>No shadow events recorded yet.</p>
            <p className="text-xs mt-1">Events will appear as users consume content.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AdminECCPage = () => {
  const { notifyError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('models');
  
  // Models state
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  
  // Track D: Draft Editor Modal state
  const [editingModel, setEditingModel] = useState(null);
  const [showDraftEditor, setShowDraftEditor] = useState(false);
  
  // Analytics state
  const [summaryStats, setSummaryStats] = useState(null);
  const [dailyBreakdown, setDailyBreakdown] = useState([]);
  
  // Simulation state
  const [simulating, setSimulating] = useState(false);
  const [simulationResult, setSimulationResult] = useState(null);
  const [showLegacyComparison, setShowLegacyComparison] = useState(true);
  const [pricingProjection, setPricingProjection] = useState(null);
  const [loadingPricingProjection, setLoadingPricingProjection] = useState(false);
  
  const [scenario, setScenario] = useState({
    model_id: '',
    scenario_name: 'Default Scenario',
    period_days: 30,
    total_users: 1000,
    active_user_pct: 0.3,
    avg_listen_minutes_per_day: 30,
    avg_questions_per_day: 2,
    avg_response_chars: 500,
    subscriber_pct: 0.2,
    credit_price_usd: 0.10,
    subscriber_credit_price_usd: 0.08,
    // Track E1: Simulation mode
    simulation_mode: 'distribution',
    // Track E1: Tier distribution
    a_la_carte_pct: 0.15,
    novice_pct: 0.30,
    pro_reader_pct: 0.40,
    book_worm_pct: 0.15,
    // Track E1: Tier multipliers
    multiplier_a_la_carte: 0.55,
    multiplier_novice: 0.75,
    multiplier_pro_reader: 1.00,
    multiplier_book_worm: 1.35,
    // Track E1: Single tier override
    single_tier: 'pro_reader',
    // Track E2: Bundle offset
    bundle_offset_enabled: true,
    bundle_purchase_likelihood: 0.60,
    bundle_coverage_ratio: 0.75,
    // Track E4: Cost inputs (for Cost vs Value panel)
    tts_cost_per_1k_chars: 0.015,
    llm_cost_per_1k_chars: 0.005
  });

  const getAuthToken = () => localStorage.getItem('token') || localStorage.getItem('accessToken');

  useEffect(() => {
    loadModels();
    loadAnalytics();
  }, []);

  const loadModels = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await axios.get(`${API}/admin/ecc/models`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModels(response.data.models || []);
      const draftModel = response.data.models?.find(m => m.status === 'draft');
      if (draftModel) {
        setScenario(prev => ({ ...prev, model_id: draftModel.id }));
        setSelectedModel(draftModel);
      }
    } catch (error) {
      console.error('Failed to load models:', error);
      notifyError('Failed to load economic models');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const token = getAuthToken();
      const [summaryRes, dailyRes] = await Promise.all([
        axios.get(`${API}/admin/ecc/analytics/summary?days=7`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { summary: null } })),
        axios.get(`${API}/admin/ecc/analytics/daily?days=7`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { daily_breakdown: [] } }))
      ]);
      setSummaryStats(summaryRes.data.summary);
      setDailyBreakdown(dailyRes.data.daily_breakdown || []);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  };

  const runSimulation = async () => {
    if (!scenario.model_id) {
      notifyError('Please select a model for simulation');
      return;
    }
    try {
      setSimulating(true);
      setPricingProjection(null);
      const token = getAuthToken();
      const response = await axios.post(`${API}/admin/ecc/simulate`, scenario, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const simulationData = response.data.simulation;
      setSimulationResult(simulationData);
      console.log('[SUCCESS] ' + 'Simulation complete');
      if (simulationData) {
        await fetchPricingProjection(simulationData);
      }
    } catch (error) {
      console.error('Simulation failed:', error);
      // Handle Pydantic validation errors which may be arrays of objects
      const detail = error.response?.data?.detail;
      let errorMsg = 'Simulation failed';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic validation error - extract message from first error
        errorMsg = detail[0]?.msg || detail[0]?.message || 'Validation error';
      } else if (detail?.msg) {
        errorMsg = detail.msg;
      }
      notifyError(errorMsg);
    } finally {
      setSimulating(false);
    }
  };

  const fetchPricingProjection = async (simulationData) => {
    try {
      setLoadingPricingProjection(true);
      const token = getAuthToken();
      const projectionRequest = {
        model_id: scenario.model_id,
        period_days: scenario.period_days,
        total_users: scenario.total_users,
        active_user_pct: scenario.active_user_pct,
        avg_listen_minutes_per_day: scenario.avg_listen_minutes_per_day,
        avg_questions_per_day: scenario.avg_questions_per_day,
        avg_response_chars: scenario.avg_response_chars,
        credit_price_usd: scenario.credit_price_usd,
        subscriber_credit_price_usd: scenario.subscriber_credit_price_usd,
        total_ecc_credits: simulationData.consumption?.total_ecc_reader_credits || 0,
        total_cost_usd: simulationData.financials?.total_cost_usd || 0,
        gross_revenue_usd: simulationData.financials?.gross_revenue_usd || 0
      };
      const response = await axios.post(`${API}/admin/ecc/simulations/pricing-projection`, projectionRequest, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPricingProjection(response.data);
    } catch (error) {
      console.error('Pricing projection failed:', error);
    } finally {
      setLoadingPricingProjection(false);
    }
  };

  const updateModelIntent = async (modelId, intentData) => {
    const token = getAuthToken();
    await axios.patch(`${API}/admin/ecc/models/${modelId}`, intentData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    loadModels();
  };

  // Track D: Save draft model parameters
  const saveDraftModel = async (modelId, params) => {
    const token = getAuthToken();
    await axios.patch(`${API}/admin/ecc/models/${modelId}`, params, {
      headers: { Authorization: `Bearer ${token}` }
    });
    await loadModels();
  };

  // Track D: Open draft editor modal
  const handleEditDraft = (model) => {
    setEditingModel(model);
    setShowDraftEditor(true);
  };

  // Track D: Close draft editor modal
  const handleCloseDraftEditor = () => {
    setShowDraftEditor(false);
    setEditingModel(null);
  };

  const tabs = [
    { id: 'models', label: 'Models', icon: Settings },
    { id: 'simulate', label: 'Simulate', icon: Play },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <AdminNav />
      <AdminTopMenuBar />
      
      <div className="pb-20 md:pb-0">
        {/* Header */}
        <header className="bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 border-b border-slate-800">
          <div className="mx-auto max-w-7xl px-4 py-10 sm:py-12">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Calculator className="w-8 h-8 text-purple-400" />
                  <h1 className="text-3xl sm:text-4xl font-bold">Economic Control Center</h1>
                </div>
                <p className="text-sm sm:text-base text-slate-300 max-w-2xl">
                  Analyze and simulate economic models. Shadow calculations for analysis only — no runtime impact on users or pricing.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="warning">Simulation Only</Badge>
                <button onClick={loadModels} className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg border border-slate-600 transition-colors">
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </header>
        
        {/* Main Content */}
        <main className="mx-auto max-w-7xl px-4 py-8">
          {/* Non-authoritative Banner */}
          <div className="flex items-center gap-3 px-4 py-3 bg-amber-500/10 border border-amber-500/20 rounded-xl mb-8">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div className="text-sm">
              <span className="font-semibold text-amber-400">ECC is observational only</span>
              <span className="text-amber-300/80"> — All values shown are shadow calculations for analysis. Phase 4 authority requires explicit approval.</span>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-slate-800 pb-4">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-purple-600 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
          
          {/* Tab Content */}
          {activeTab === 'models' && (
            <div className="space-y-6">
              <SectionHeader icon={Database} title="Economic Models" color="text-purple-400" />
              
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
                </div>
              ) : (
                <div className="space-y-4">
                  {models.map(model => (
                    <ModelCard
                      key={model.id}
                      model={model}
                      isSelected={selectedModel?.id === model.id}
                      onSelect={setSelectedModel}
                      onUpdateIntent={updateModelIntent}
                      onEditDraft={handleEditDraft}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'simulate' && (
            <SimulationPanel
              models={models}
              scenario={scenario}
              setScenario={setScenario}
              onRunSimulation={runSimulation}
              simulating={simulating}
              simulationResult={simulationResult}
              pricingProjection={pricingProjection}
              loadingPricingProjection={loadingPricingProjection}
              showLegacyComparison={showLegacyComparison}
              setShowLegacyComparison={setShowLegacyComparison}
            />
          )}
          
          {activeTab === 'analytics' && (
            <AnalyticsSection
              summaryStats={summaryStats}
              dailyBreakdown={dailyBreakdown}
              onRefresh={loadAnalytics}
            />
          )}
        </main>
      </div>
      
      {/* Track D: Draft Model Editor Modal */}
      <DraftModelEditor
        model={editingModel}
        isOpen={showDraftEditor}
        onClose={handleCloseDraftEditor}
        onSave={saveDraftModel}
      />
      
      <UniversalFooter />
    </div>
  );
};

export default AdminECCPage;
